import { z } from 'zod';
import { formatInteger } from './format.js';
/**
 * Property/unit data model extension (owner decision 12.10.2026, migration 0029): the vocabulary of the
 * new unit columns (subtype, usage, external availability, energy data, amenities), of the unit's
 * satellites (cost items, media, external reservations, tenancies), the availability rule that combines
 * the DAMOKLES-owned unit status with the sales release and the availability reported by an external
 * system, and the fields an external system (TeamProQ) owns while its connection is `import_only`.
 * Shared by the API (properties/reservations/deals/financier modules, the TeamProQ importer) and the SPA.
 */
// ---------------------------------------------------------------------------------------------
// Enums of migration 0029 with German labels
// ---------------------------------------------------------------------------------------------
export const UNIT_SUBTYPES = [
    'apartment',
    'ground_floor_apartment',
    'attic_apartment',
    'maisonette',
    'penthouse',
    'loft',
    'parking_space',
    'underground_parking_space',
    'outdoor_parking_space',
    'garage',
    'carport',
    'storage',
    'office',
    'retail',
    'other',
];
export const unitSubtypeLabels = {
    apartment: 'Etagenwohnung',
    ground_floor_apartment: 'Erdgeschosswohnung',
    attic_apartment: 'Dachgeschosswohnung',
    maisonette: 'Maisonette',
    penthouse: 'Penthouse',
    loft: 'Loft',
    parking_space: 'Stellplatz',
    underground_parking_space: 'Tiefgaragenstellplatz',
    outdoor_parking_space: 'Außenstellplatz',
    garage: 'Garage',
    carport: 'Carport',
    storage: 'Lager / Abstellraum',
    office: 'Büro',
    retail: 'Laden / Einzelhandel',
    other: 'Sonstiges',
};
export const UNIT_USAGES = [
    'residential',
    'serviced_apartment',
    'student_housing',
    'senior_living',
    'commercial',
    'other',
];
export const unitUsageLabels = {
    residential: 'Wohnen',
    serviced_apartment: 'Serviced Apartment',
    student_housing: 'Studentenwohnen',
    senior_living: 'Seniorenwohnen / Pflege',
    commercial: 'Gewerbe',
    other: 'Sonstige Nutzung',
};
export const UNIT_EXTERNAL_BLOCKS = ['free', 'reserved', 'exclusive', 'sold'];
export const unitExternalBlockLabels = {
    free: 'Frei',
    reserved: 'Extern reserviert',
    exclusive: 'Extern exklusiv reserviert',
    sold: 'Extern verkauft',
};
export const ENERGY_CERTIFICATE_TYPES = ['consumption', 'demand'];
export const energyCertificateTypeLabels = {
    consumption: 'Verbrauchsausweis',
    demand: 'Bedarfsausweis',
};
export const ENERGY_CLASSES = ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
export const UNIT_COST_ITEM_KINDS = ['incidental_purchase', 'monthly'];
export const unitCostItemKindLabels = {
    incidental_purchase: 'Kaufnebenkosten',
    monthly: 'Laufende Kosten mtl.',
};
export const UNIT_COST_ITEM_TAX_TREATMENTS = [
    'depreciable',
    'deductible_first_year',
    'deductible',
    'none',
];
export const unitCostItemTaxTreatmentLabels = {
    depreciable: 'Abschreibungsfähig',
    deductible_first_year: 'Im ersten Jahr absetzbar',
    deductible: 'Laufend absetzbar',
    none: 'Steuerlich nicht relevant',
};
export const UNIT_LINK_KINDS = ['bundle'];
export const unitLinkKindLabels = { bundle: 'Gemeinsam verkauft' };
export const MEDIA_ITEM_KINDS = ['photo', 'floor_plan', 'other'];
export const mediaItemKindLabels = {
    photo: 'Bild',
    floor_plan: 'Grundriss',
    other: 'Sonstiges',
};
export const EXTERNAL_RESERVATION_STATUSES = [
    'request',
    'active',
    'exclusive',
    'sold',
    'expired',
    'canceled',
    'denied',
];
export const externalReservationStatusLabels = {
    request: 'Angefragt',
    active: 'Reserviert',
    exclusive: 'Exklusiv reserviert',
    sold: 'Verkauft',
    expired: 'Abgelaufen',
    canceled: 'Storniert',
    denied: 'Abgelehnt',
};
export const TENANCY_STATUSES = ['active', 'terminated', 'ended'];
export const tenancyStatusLabels = {
    active: 'Laufend',
    terminated: 'Gekündigt',
    ended: 'Beendet',
};
export const EXTERNAL_SOURCES = ['teamproq', 'wla_dialer', 'kvr'];
export const externalSourceLabels = {
    teamproq: 'TeamProQ',
    wla_dialer: 'WLA-Dialer',
    kvr: 'KV-Rational',
};
export const EXTERNAL_REF_ENTITIES = [
    'property',
    'unit',
    'external_reservation',
    'tenancy',
    'document',
    'media_item',
    'unit_cost_item',
    'contact',
    'campaign',
    'timeline_event',
    'appointment',
];
// ---------------------------------------------------------------------------------------------
// Amenity catalog (covers every value of the TeamProQ REST API v1 enums `Unit.amenities.*`,
// `energyInformation.energyCarriers`, `energyInformation.heatingSystems`, KfW standards)
// ---------------------------------------------------------------------------------------------
export const AMENITY_CATALOG = {
    rooms: {
        basement: 'Keller',
        sauna: 'Sauna',
        loggia: 'Loggia',
        terrace: 'Terrasse',
        balcony: 'Balkon',
        storageRoom: 'Abstellraum',
    },
    kitchen: {
        builtInKitchen: 'Einbauküche',
        kitchenette: 'Kochnische',
        kitchenInLivingRoom: 'Wohnküche',
        withWindow: 'Küche mit Fenster',
        openKitchen: 'Offene Küche',
    },
    bathroom: {
        shower: 'Dusche',
        bathtub: 'Badewanne',
        withWindow: 'Bad mit Fenster',
        guestToilet: 'Gäste-WC',
    },
    floor: {
        tiles: 'Fliesen',
        carpet: 'Teppichboden',
        pvc: 'PVC',
        parquet: 'Parkett',
        stone: 'Stein',
        laminate: 'Laminat',
        design: 'Designboden',
        linoleum: 'Linoleum',
        vinyl: 'Vinyl',
        planks: 'Dielen',
    },
    other: {
        wheelchairAccessible: 'Rollstuhlgerecht',
        accessible: 'Barrierefrei',
        petsAllowed: 'Haustiere erlaubt',
        sharedGarden: 'Gartenmitbenutzung',
        suitableAsSharedFlat: 'WG-geeignet',
        requiresPermitForSubsidizedHousing: 'Wohnberechtigungsschein erforderlich',
        lift: 'Personenaufzug',
        goodsLift: 'Lastenaufzug',
        fireplaceOrOven: 'Kamin / Ofen',
        solarPowerSystem: 'Photovoltaikanlage',
        batteryStorage: 'Batteriespeicher',
        bicycleStorageRoom: 'Fahrradraum',
        parkingSite: 'Stellplatz vorhanden',
        withBasement: 'Mit Keller',
        airConditioning: 'Klimaanlage',
        blinds: 'Rollläden',
    },
};
export const AMENITY_GROUPS = [
    'rooms',
    'kitchen',
    'bathroom',
    'floor',
    'other',
];
export const amenityGroupLabels = {
    rooms: 'Räume',
    kitchen: 'Küche',
    bathroom: 'Bad',
    floor: 'Bodenbelag',
    other: 'Sonstiges',
};
const amenityKeys = (group) => Object.keys(AMENITY_CATALOG[group]);
const amenityList = (group) => z
    .array(z.enum(amenityKeys(group)))
    .max(Object.keys(AMENITY_CATALOG[group]).length)
    .refine((values) => new Set(values).size === values.length, { message: 'Doppelte Ausstattungsmerkmale.' })
    .optional();
/** Shape of `unit.amenities` (jsonb): per group a list of catalog keys. */
export const amenitiesSchema = z
    .object({
    rooms: amenityList('rooms'),
    kitchen: amenityList('kitchen'),
    bathroom: amenityList('bathroom'),
    floor: amenityList('floor'),
    other: amenityList('other'),
})
    .strict();
/** German labels of the selected amenities in catalog order („Balkon, Keller, Einbauküche …"). */
export function amenityLabels(amenities) {
    if (!amenities)
        return [];
    const labels = [];
    for (const group of AMENITY_GROUPS) {
        const selected = new Set(amenities[group] ?? []);
        for (const [key, label] of Object.entries(AMENITY_CATALOG[group])) {
            if (selected.has(key))
                labels.push(label);
        }
    }
    return labels;
}
export const ENERGY_CARRIER_LABELS = {
    gas: 'Gas',
    oil: 'Öl',
    solar: 'Solar',
    coal: 'Kohle',
    electric: 'Strom',
    geothermal: 'Erdwärme',
    hotAir: 'Warmluft',
    pellets: 'Pellets',
    other: 'Sonstiger Energieträger',
};
export const ENERGY_CARRIERS = Object.keys(ENERGY_CARRIER_LABELS);
export const HEATING_SYSTEM_LABELS = {
    systemForWholeFloor: 'Etagenheizung',
    central: 'Zentralheizung',
    oven: 'Ofenheizung',
    gas: 'Gasheizung',
    oil: 'Ölheizung',
    electric: 'Elektroheizung',
    pellets: 'Pelletheizung',
    heatedFloor: 'Fußbodenheizung',
    districtHeating: 'Fernwärme',
    solar: 'Solarheizung',
    blockHeatingStation: 'Blockheizkraftwerk',
    nightStorageHeater: 'Nachtspeicherofen',
    heatPump: 'Wärmepumpe',
};
export const HEATING_SYSTEMS = Object.keys(HEATING_SYSTEM_LABELS);
/** KfW efficiency house standards (TeamProQ `KfWEfficiencyHouse.standard`); stored as text in `unit.kfw_standard`. */
export const KFW_STANDARD_LABELS = {
    '40NH': 'KfW-Effizienzhaus 40 NH',
    '40Plus': 'KfW-Effizienzhaus 40 Plus',
    '40QNG': 'KfW-Effizienzhaus 40 QNG',
    '40': 'KfW-Effizienzhaus 40',
    '55Plus': 'KfW-Effizienzhaus 55 Plus',
    '55NH': 'KfW-Effizienzhaus 55 NH',
    '55': 'KfW-Effizienzhaus 55',
    '70': 'KfW-Effizienzhaus 70',
    '70NH': 'KfW-Effizienzhaus 70 NH',
    '85': 'KfW-Effizienzhaus 85',
    '85NH': 'KfW-Effizienzhaus 85 NH',
    '100': 'KfW-Effizienzhaus 100',
    '115': 'KfW-Effizienzhaus 115',
    Monument: 'KfW-Effizienzhaus Denkmal',
    Monument_NH: 'KfW-Effizienzhaus Denkmal NH',
};
export const PRICE_COMPOSITION_FIELDS = [
    'priceLandCents',
    'priceExistingBuildingCents',
    'priceNewConstructionCents',
    'renovationCents',
    'priceFurnitureCents',
    'priceOutdoorFacilitiesCents',
];
const roundPct = (value) => Math.round(value * 1000) / 1000;
/**
 * Land share and old-building share of the purchase price in percent (3 decimals), derived from the
 * price composition. The base is `totalCents` (the unit's purchase price) when it is positive, else
 * the sum of the given parts. Each share is null when its part is missing or there is no positive base.
 */
export function derivePriceShares(composition, totalCents) {
    const parts = PRICE_COMPOSITION_FIELDS.map((key) => composition[key]).filter((v) => typeof v === 'number');
    const base = typeof totalCents === 'number' && totalCents > 0 ? totalCents : parts.reduce((a, b) => a + b, 0);
    const share = (part) => typeof part === 'number' && base > 0 ? Math.min(100, roundPct((part / base) * 100)) : null;
    return {
        landSharePct: share(composition.priceLandCents),
        oldBuildingSharePct: share(composition.priceExistingBuildingCents),
    };
}
export const UNIT_AVAILABILITIES = [
    'available',
    'reserved',
    'sold',
    'unavailable',
    'not_released',
    'external_reserved',
    'external_exclusive',
    'external_sold',
];
export const unitAvailabilityLabels = {
    available: 'Frei',
    reserved: 'Reserviert',
    sold: 'Verkauft',
    unavailable: 'Nicht verfügbar',
    not_released: 'Nicht freigegeben',
    external_reserved: 'Extern reserviert',
    external_exclusive: 'Extern exklusiv reserviert',
    external_sold: 'Extern verkauft',
};
/**
 * One availability for display and selection. The DAMOKLES status wins when it is not `available`
 * (a reservation/notarization/archiving in DAMOKLES is the stronger fact); a free unit is then checked
 * against the sales release and the external block.
 */
export function unitAvailability(u) {
    if (u.status !== 'available')
        return u.status;
    if (!u.salesReleased)
        return 'not_released';
    switch (u.externalBlock) {
        case 'reserved':
            return 'external_reserved';
        case 'exclusive':
            return 'external_exclusive';
        case 'sold':
            return 'external_sold';
        case 'free':
        case null:
            return 'available';
    }
}
/** Reservable = status available AND released for sale AND no external block. */
export function isUnitReservable(u) {
    return unitAvailability(u) === 'available';
}
// ---------------------------------------------------------------------------------------------
// Fields owned by an external system while its connection is `import_only`
// ---------------------------------------------------------------------------------------------
export const MANAGED_UNIT_FIELDS = {
    teamproq: [
        'number',
        'type',
        'subtype',
        'usage',
        'street',
        'houseNumber',
        'floor',
        'floorPosition',
        'rooms',
        'bathrooms',
        'areaSqm',
        'landAreaSqm',
        'priceCents',
        'priceLandCents',
        'priceExistingBuildingCents',
        'priceNewConstructionCents',
        'priceFurnitureCents',
        'priceOutdoorFacilitiesCents',
        'renovationCents',
        'landSharePct',
        'oldBuildingSharePct',
        'amenities',
        'energyCertificateType',
        'energyValueKwhSqm',
        'energyClass',
        'energyCarriers',
        'heatingSystems',
        'kfwStandard',
        'externalBlock',
    ],
    wla_dialer: [],
    kvr: [],
};
/**
 * Unit fields delivered by TeamProQ, owned by DAMOKLES — written only while the DAMOKLES value is empty
 * (owner decision 02.11.2026, D-404): Soll-Miete, AfA-Satz and Miteigentumsanteil are filled by an import while
 * the unit's value is still empty, the Vertriebsfreigabe only when the import creates the unit. They are never
 * locked; Nico maintains them in DAMOKLES.
 */
export const TEAMPROQ_SEEDED_UNIT_FIELDS = [
    'rentTargetCents',
    'depreciationPct',
    'coOwnershipShare',
    'salesReleased',
];
export const MANAGED_PROPERTY_FIELDS = {
    teamproq: [
        'name',
        'street',
        'houseNumber',
        'postalCode',
        'city',
        'yearBuiltFrom',
        'yearBuiltUntil',
        'yearRenovated',
        'coOwnershipSharesTotal',
        'locationDescription',
        'surroundingsDescription',
        'objectDescription',
        'specialFeatures',
        'additionalInformation',
    ],
    // The WLA dialer delivers contacts, calls and notes only — no property or unit fields.
    wla_dialer: [],
    kvr: [],
};
/** True when an imported value counts as delivered: not null/undefined and, for text, not blank. */
export function isDeliveredValue(value) {
    if (value === null || value === undefined)
        return false;
    if (typeof value === 'string')
        return value.trim() !== '';
    return true;
}
/**
 * Managed-field rule of a property (Nico feedback FB-004, owner decision 29.10.2026, D-318): a managed field is
 * locked only when the last import actually delivered a value for it (`external_ref.imported_values`). A managed
 * field TeamProQ leaves empty stays editable in DAMOKLES until a later import delivers it — that import overwrites
 * the DAMOKLES value and locks the field again. Order follows `managed`.
 */
export function deliveredManagedFields(managed, importedValues) {
    return managed.filter((key) => isDeliveredValue(importedValues[key]));
}
/** Required base data in the order of the onboarding sheet. */
export const BASE_DATA_REQUIRED = [
    { key: 'yearBuiltFrom', label: 'Baujahr' },
    { key: 'livingAreaTotalSqm', label: 'Wohnfläche gesamt' },
    { key: 'energyCertificate', label: 'Energieausweis' },
    { key: 'heating', label: 'Heizung' },
    { key: 'serviceChargePerSqmCents', label: 'Hausgeld Ø je m² mtl.' },
    { key: 'parkingSpacesCount', label: 'Stellplätze (Anzahl)' },
    { key: 'readyForOccupancy', label: 'Bezugsfertig' },
    { key: 'notary', label: 'Notar' },
    { key: 'managerUserId', label: 'Objektbetreuung' },
    { key: 'state', label: 'Bundesland' },
    { key: 'landTransferTaxPct', label: 'Grunderwerbsteuer' },
    { key: 'calcDepreciationPct', label: 'AfA-Satz p. a.' },
    { key: 'calcBuildingSharePct', label: 'Gebäudeanteil' },
    { key: 'calcInterestPct', label: 'Sollzins p. a.' },
    { key: 'calcRepaymentPct', label: 'Tilgung p. a.' },
    { key: 'calcSevCents', label: 'SE-Verwaltung' },
    { key: 'calcWegCents', label: 'WEG-Verwaltung' },
    { key: 'calcReservePerSqmCents', label: 'Instandhaltungsrücklage' },
    { key: 'calcOtherCostsPerSqmCents', label: 'Sonstige nicht umlagefähige Kosten' },
];
/** Optional base data (shown, never counted). */
export const BASE_DATA_OPTIONAL = [
    { key: 'yearRenovated', label: 'Sanierungsjahr' },
    { key: 'parkingSpacePriceCents', label: 'Preis je Stellplatz' },
    { key: 'declarationOfDivisionOn', label: 'Teilungserklärung vom' },
    { key: 'coOwnershipSharesTotal', label: 'MEA gesamt' },
    { key: 'documentsUrl', label: 'Externer Unterlagenordner' },
];
export const BASE_DATA_STATUSES = ['complete', 'partial', 'empty'];
/** List pill „Stammdaten" (Ampel). */
export const baseDataStatusLabels = {
    complete: 'vollständig',
    partial: 'teilweise',
    empty: 'fehlt',
};
/**
 * „Grunderwerbsteuer" counts as given when the property has its own rate or a federal state (the state's rate
 * applies) — only the fallback rate of an unknown state is missing data.
 */
function baseDataDone(values, key) {
    if (key === 'landTransferTaxPct')
        return values.landTransferTaxPct !== null || values.state !== null;
    return isDeliveredValue(values[key]);
}
export function baseDataCompleteness(values) {
    const required = BASE_DATA_REQUIRED.map(({ key, label }) => ({
        key,
        label,
        done: baseDataDone(values, key),
    }));
    const optional = BASE_DATA_OPTIONAL.map(({ key, label }) => ({
        key,
        label,
        done: baseDataDone(values, key),
    }));
    const doneCount = required.filter((item) => item.done).length;
    const totalCount = required.length;
    const status = doneCount === totalCount ? 'complete' : doneCount > 0 ? 'partial' : 'empty';
    return { required, optional, doneCount, totalCount, status };
}
// ---------------------------------------------------------------------------------------------
// Unit counts by effective availability (property list, record metrics, units table tabs)
// ---------------------------------------------------------------------------------------------
/**
 * Display buckets of a unit. Precedence: DAMOKLES status `sold` / `reserved` / `unavailable` first, then
 * the external block (`sold` → sold, `reserved` → reserved, `exclusive` → exclusive), then the sales
 * release (`not_released`), else `available` (= `isUnitReservable`).
 */
export const UNIT_COUNT_BUCKETS = [
    'available',
    'reserved',
    'exclusive',
    'sold',
    'not_released',
    'unavailable',
];
export const unitCountBucketLabels = {
    available: 'Frei',
    reserved: 'Reserviert',
    exclusive: 'Exklusiv',
    sold: 'Verkauft',
    not_released: 'Nicht freigegeben',
    unavailable: 'Nicht verfügbar',
};
/** The bucket of one unit and whether it comes from the external system (TeamProQ). */
export function unitCountBucket(u) {
    if (u.status === 'sold' || u.status === 'reserved' || u.status === 'unavailable')
        return { bucket: u.status, external: false };
    if (u.externalBlock === 'sold')
        return { bucket: 'sold', external: true };
    if (u.externalBlock === 'reserved')
        return { bucket: 'reserved', external: true };
    if (u.externalBlock === 'exclusive')
        return { bucket: 'exclusive', external: true };
    if (!u.salesReleased)
        return { bucket: 'not_released', external: false };
    return { bucket: 'available', external: false };
}
export function emptyUnitCounts() {
    return {
        total: 0,
        available: 0,
        reserved: 0,
        exclusive: 0,
        sold: 0,
        notReleased: 0,
        unavailable: 0,
        externalReserved: 0,
        externalSold: 0,
    };
}
/** Adds `n` units of a bucket (`external_reserved` / `external_sold` count into reserved/sold and their part). */
export function addUnitCount(counts, key, n) {
    counts.total += n;
    switch (key) {
        case 'external_reserved':
            counts.reserved += n;
            counts.externalReserved += n;
            break;
        case 'external_sold':
            counts.sold += n;
            counts.externalSold += n;
            break;
        case 'not_released':
            counts.notReleased += n;
            break;
        case 'available':
        case 'reserved':
        case 'exclusive':
        case 'sold':
        case 'unavailable':
            counts[key] += n;
            break;
    }
    return counts;
}
/**
 * „34 Einheiten · 14 frei · 6 reserviert · 2 exklusiv · 12 verkauft · 3 nicht freigegeben" — exklusiv and
 * nicht freigegeben only when > 0.
 */
export function unitCountsSummary(counts) {
    const n = formatInteger;
    const parts = [
        `${n(counts.total)} ${counts.total === 1 ? 'Einheit' : 'Einheiten'}`,
        `${n(counts.available)} frei`,
        `${n(counts.reserved)} reserviert`,
    ];
    if (counts.exclusive > 0)
        parts.push(`${n(counts.exclusive)} exklusiv`);
    parts.push(`${n(counts.sold)} verkauft`);
    if (counts.notReleased > 0)
        parts.push(`${n(counts.notReleased)} nicht freigegeben`);
    return parts.join(' · ');
}
//# sourceMappingURL=property-data.js.map